<script lang="ts" setup>
  import { useQuery } from '@tanstack/vue-query'

  import { useOrderService, useProductsService } from '~/core/services'
  import type { FetchErrorObj, Nullable } from '~/core/types'
  import { BngModal, BngPageFooter } from '~/layouts/components'

  const { displayToast } = useUiStore()
  const router = useRouter()
  const route = useRoute()
  const { t } = useI18n()
  const { refetchCart } = useCartStore()
  const { postProduct } = useOrderService()
  const { getProduct } = useProductsService()
  const { layout } = usePartner()

  const { matches: matchDesktop } = useMatchMedia(ScreenWidth.desktop)

  const selectedGolfId = ref<Nullable<number>>(null)
  const isScrolled = ref(false)
  const showReservationBanner = ref(false)

  const radioItems = ref(['products.at-golf', 'products.online'])
  const radioIndex = ref(0)

  const bookingPlace = ref('online')

  const productId = computed(() => parseInt(route.params.id as string))
  function onRadioInput(index: number) {
    radioIndex.value = index
    bookingPlace.value = index === 0 ? 'desk' : 'online'
  }

  const selectedGolfName = computed(() => {
    const golf = product.value?.data?.golfs.find(
      (g: { id: number, name: string }) => g.id === selectedGolfId.value,
    )
    return golf ? golf.name : null
  })

  function closeModal() {
    router.replace({ ...route, query: { ...route.query, modal: undefined } })
  }

  async function handleAddToCart() {
    try {
      if (!product.value?.data) {
        throw Error('Product not defined')
      }
      if (product.value.data.golfs.length > 1 && !selectedGolfId.value && ['referent', 'choose'].includes(product.value.data.golf_selection_type)) {
        throw Error('No golf selected')
      }
      await postProduct(product.value.data.id, {
        golf_id: product.value.data.golfs.length > 1 ? selectedGolfId.value as number : product.value.data.golfs[0]?.id ?? null,
        booking_place: bookingPlace.value,
      })
      refetchCart()
      await router.push('/shopping-cart/basket')
    } catch (e) {
      const { status, message } = e as FetchErrorObj<unknown>
      console.error(e)
      displayToast(status === 406 ? message : 'Une erreur est survenue.', 'error', 3000)
    }
  }

  function onGolfClick(id: number) {
    selectedGolfId.value = id
    closeModal()
  }

  async function fetchProduct(productId: number) {
    try {
      return await getProduct(productId)
    } catch (e) {
      const { status } = e as FetchErrorObj<unknown>
      if (status === 404) {
        displayToast('Produit introuvable', 'warning', 3000)
        router.push('/')
        return null
      }
      throw e
    }
  }

  function onIntersectionObserverEvent(intersectionObserverEntries: IntersectionObserverEntry[]) {
    const intersectionEvent = intersectionObserverEntries[0]
    isScrolled.value = intersectionEvent.intersectionRatio === 0
  }

  const canFetchProduct = computed(() => !isNaN(productId.value))
  const { data: product } = useQuery({
    enabled: canFetchProduct,
    queryKey: ['product', productId],
    queryFn: () => fetchProduct(productId.value),
  })

  const finalPrice = computed(() => `${product.value?.data?.final_price?.toFixed(2)}€`)

  onMounted(() => {
    const observer = new IntersectionObserver(onIntersectionObserverEvent, {
      root: document.getElementById('layout-observer-root'),
      rootMargin: '-48px 0px 0px 0px',
      threshold: [0],
    })
    const pictureElement = document.getElementById('product-picture') as HTMLElement
    if (pictureElement) {
      observer.observe(pictureElement)
    }
  })

  useHead({
    title: product.value?.data?.name ? `Bookandgolf | ${product.value.data.name}` : 'Bookandgolf | Produit',
    meta: [{
      name: 'description',
      content:
        'Découvrez l\'ensemble de nos produits Practice, Pass&Carnet et Enseignement sur notre site',
    }],
  })
</script>
<template>
  <div class="p-product">
    <ProductGolfDetailBanner
      :class="{ sticky: isScrolled }"
      :has-favorite="false"
    />
    <div
      v-if="!matchDesktop"
      id="product-picture"
      class="head"
    >
      <img
        :src="product?.data?.picture"
        alt=""
      >
    </div>
    <div class="container">
      <div class="content">
        <div class="illustration-and-heading">
          <img
            :src="product?.data?.picture"
            alt=""
          >
          <div class="heading">
            <div class="product-type">
              {{ product?.data?.type }}
            </div>
            <h2>
              <span>{{ product?.data?.name }}</span>
            </h2>
            <div
              v-if="product?.data?.golfs.length === 1"
              class="golf"
            >
              <p class="golf-name">
                <SvgIcon icon="location" />
                <span>{{ product.data.golfs[0].name }}</span>
              </p>
              <NuxtLink :to="`/golf/${product.data.golfs[0].id}`">
                {{ t("products.display-golf") }}
              </NuxtLink>
            </div>
            <div
              v-if="product?.data?.golfs && product.data.golfs.length > 1"
              class="golfs"
            >
              <div class="golf-name">
                <SvgIcon icon="location" />
                <span>{{ t("products.on-n-golfs", product.data.golfs.length) }}</span>
              </div>
              <div class="link">
                <NuxtLink to="?modal=golflist">
                  {{ t("products.display-partners-list") }}
                </NuxtLink>
              </div>
              <Teleport
                v-if="route.query.modal === 'golflist'"
                to="#modal"
              >
                <BngModal
                  :closable="true"
                  foreground
                  @close="closeModal"
                >
                  <ProductGolfList
                    title="Golfs partenaires"
                    :golfs="product.data.golfs"
                    @on-golf-click="(id: number) => $router.push(`/golf/${id}`)"
                  />
                </BngModal>
              </Teleport>
            </div>
          </div>
        </div>
        <Divider />
        <div
          v-if="product?.data?.description"
          class="description"
        >
          <div class="title">
            {{ t("products.product-details") }}
          </div>
          <div v-html="product.data.description" />
        </div>
        <Divider v-if="product?.data?.terms_of_use && product.data.terms_of_use !== ''" />
        <div
          v-if="product?.data?.terms_of_use && product.data.terms_of_use !== ''"
          class="description"
        >
          <div class="title">
            {{ t("products.term-of-use") }}
          </div>
          <div
            class="detail"
            v-html="product.data.terms_of_use"
          />
        </div>
      </div>
      <div class="reservation">
        <div
          v-if="product?.data?.on_sale"
          class="golf-selector"
        >
          <!-- Si selection à un nom -->
          <div
            v-if="selectedGolfName"
            class="selected-golf"
          >
            <span class="golf-name">
              <SvgIcon icon="location" />
              <span>{{ selectedGolfName }}</span>
            </span>
            <span>
              <NuxtLink to="?modal=golf-selection">{{ t("common.edit") }}</NuxtLink>
            </span>
          </div>
          <!-- Si pas de selection et que le mode est referent ou choose -->
          <div
            v-if="!selectedGolfId && ['referent', 'choose'].includes(product.data.golf_selection_type)"
            class="golf-selection-type"
          >
            <div class="warning-message">
              <SvgIcon
                icon="danger"
                :height="32"
                :width="38"
              />
              <span class="text">
                {{ t( product.data.golf_selection_type === "referent" ? "products.please-select-referent-golf" : "products.please-select-golf" ) }}
              </span>
            </div>
            <NuxtLink
              to="?modal=golf-selection"
              class="add-to-cart"
            >
              <span class="title">
                {{ t("products.select-golf") }}
                <SvgIcon
                  icon="arrow-nav"
                  :width="24"
                  :height="20"
                />
              </span>
              <div class="prices">
                <span
                  v-if="product.data?.final_price"
                  class="final"
                >
                  {{ finalPrice }}
                </span>
              </div>
            </NuxtLink>
          </div>

          <!-- ?? -->
          <div
            v-if="product.data.book_online && product.data.book_at_desk"
            class="product-usage"
          >
            <div>{{ t("products.please-select-usage") }}</div>
            <InputRadioSelect
              :items="radioItems"
              :selected-index="radioIndex"
              @on-input="onRadioInput"
            />
          </div>

          <!-- Si ID sélectionné ou que le mode est none -->
          <div
            v-if="selectedGolfId || !['referent', 'choose'].includes(product.data.golf_selection_type)"
            class="add-to-cart"
            @click="() => handleAddToCart()"
          >
            <span class="title">
              {{ t("products.add-to-cart") }}
              <SvgIcon
                icon="arrow-nav"
                :width="24"
                :height="20"
              />
            </span>
            <div class="prices">
              <span
                v-if="product.data.final_price"
                class="final"
              >{{ finalPrice }}</span>
            </div>
          </div>
        </div>
        <div
          v-else
          class="sold-out"
        >
          <span class="title">
            {{ t("products.sold-out") }}
          </span>
        </div>
      </div>
    </div>
    <BngPageFooter
      v-show="matchDesktop"
      v-if="layout !== 'iframe'"
    />
    <Teleport to="#reservation-banner">
      <ProductReservationConfirmationToast
        :displayed="showReservationBanner"
        @close="showReservationBanner = false"
      />
    </Teleport>
    <Teleport
      v-if="route.query.modal === 'golf-selection'"
      to="#modal"
    >
      <BngModal
        :closable="true"
        foreground
        @close="closeModal"
      >
        <ProductGolfList
          title="Choisir le golf"
          :golfs="product?.data?.golfs ?? []"
          @on-golf-click="onGolfClick"
        />
      </BngModal>
    </Teleport>
  </div>
</template>
<style scoped>
.p-product {
  display: flex;
  width: 100%;
  flex: 0 0 100%;
  flex-direction: column;
  align-items: center;

  .head {
    position: relative;
    display: flex;
    width: 100%;
    height: 200px;

    img {
      position: relative;
      z-index: 1;
      top: 0;
      width: 100%;
      height: auto;
      object-fit: cover;
    }
  }

  .c-action-banner {
    margin-bottom: 0;
  }

  .golf-name {
    display: flex;
    color: var(--color-secondary);
    font-family: Open-Sans, sans-serif;
    font-size: 0.875em;
    font-weight: 600;
    opacity: 0.8;

    .c-icon {
      &:deep(svg) {
        width: 1em;
        height: 1.125em;
      }

      margin-right: 0.25em;
    }
  }

  .container {
    position: relative;
    display: flex;
    width: 100%;
    flex: 1 1 100%;
    flex-direction: column;

    .content {
      position: relative;
      overflow: hidden;
      height: 100%;
      flex: 1 1 auto;
      padding: 1em;
      background-color: white;

      img {
        display: none;
      }

      .illustration-and-heading {
        display: flex;
        flex-direction: row;

        .heading {
          display: flex;
          flex: 1 1 auto;
          flex-direction: column;

          .product-type {
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 1.5em;
            font-weight: 600;
            opacity: 0.8;
          }

          h2 {
            margin-top: 1em;

            span {
              color: var(--color-secondary);
              font-family: Bariol, sans-serif;
              font-size: 1.5em;
              font-weight: bold;
            }
          }

          .golf {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-top: 0.5em;

            .name {
              span {
                color: var(--color-secondary);
                font-family: Open-Sans, sans-serif;
                font-size: 1em;
                font-weight: 600;
                opacity: 0.8;
              }
            }

            a {
              color: var(--color-primary);
              font-family: Open-Sans, sans-serif;
              font-size: 0.875em;
              font-weight: normal;
              text-decoration: underline;
            }
          }

          .golfs {
            margin-top: 1em;

            .link {
              display: flex;
              align-items: flex-end;
              color: var(--color-primary);
              cursor: pointer;
              font-family: Open-Sans, sans-serif;
              font-size: 0.875em;
              font-weight: normal;
              text-decoration: underline;

              a {
                margin-left: auto;
              }
            }
          }
        }
      }

      .c-divider {
        margin: 1.5em 0;
      }

      .description,
      .detail {
        color: var(--color-secondary);
        font-family: Open-Sans, sans-serif;
        font-size: 1em;
        font-weight: normal;

        .title {
          margin-bottom: 1em;
          color: var(--color-secondary);
          font-family: Bariol, sans-serif;
          font-size: 1.5em;
          font-weight: bold;
          line-height: 1.29;
          opacity: 0.8;
        }

        &:deep(p) {
          font-style: normal;

          b,
          i {
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 1em;
            font-style: normal;
            font-weight: 400;
          }

          b {
            font-weight: bold;
          }

          u {
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 1em;
            font-style: normal;
            font-weight: bold;
            text-decoration: none;
          }
        }

      }
    }
  }

  .reservation {
    position: sticky;
    bottom: 0;
    display: flex;
    width: 100%;
    flex-direction: column;
    background-color: white;
    box-shadow: var(--box-shadow-1);

    .golf-selector {
      width: 100%;
    }

    .selected-golf {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding: 1em;

      a {
        color: var(--color-secondary);
        font-family: Open-Sans, sans-serif;
        font-size: 1em;
        font-weight: 400;
        line-height: 1.33;
        opacity: 0.8;
      }

      .golf-name {
        font-size: 1em;
      }
    }

    .product-usage {
      padding: 1em;
      margin: 1em;
      background-color: var(--color-secondary-light);
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1em;
      font-weight: 400;
    }

    .add-to-cart {
      display: flex;
      width: 100%;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: 1em;
      background-color: var(--color-primary);
      cursor: pointer;

      &:hover {
        .title {
          .c-icon {
            margin-left: 0.75em;
          }
        }
      }

      .title {
        display: flex;
        flex-direction: row;
        color: var(--color-secondary);
        font-family: Bariol, sans-serif;
        font-size: 1.375em;
        font-weight: bold;
        letter-spacing: 1.5px;
        text-transform: uppercase;
        white-space: nowrap;

        .c-icon {
          margin-left: 0.5em;
          transform: rotate(-0.25turn);
          transition: margin-left ease-out 0.25s;
        }
      }

      .prices {
        display: flex;
        flex-direction: column;
        align-items: flex-end;

        .base {
          position: relative;
          margin: 0 0.375em 0.25em 0;
          color: var(--color-grey-light);
          font-family: Bariol, sans-serif;
          font-size: 0.875em;
          font-weight: normal;
          line-height: 1.66;
          text-decoration: line-through;
        }

        .final {
          margin-right: 0.5em;
          color: white;
          font-family: Bariol, sans-serif;
          font-size: 1.625em;
          font-weight: bold;
        }
      }
    }

    .golf-selection-type {
      display: flex;
      flex-direction: column;

      .warning-message {
        display: flex;
        flex-direction: row;

        .c-icon {
          width: 2em;
          height: auto;
          margin: 1.25em 1em auto;
        }
      }

      .text {
        padding: 1em 1em 1em 0;
        color: var(--color-secondary);
        font-family: Open-Sans, sans-serif;
        font-size: 1.125em;
        font-weight: 600;
        opacity: 0.8;
      }
    }

    .sold-out {
      display: flex;
      width: 100%;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      padding: 1em;
      background-color: var(--color-primary);
      cursor: not-allowed;
      filter: grayscale(1);

      .title {
        display: flex;
        flex-direction: row;
        color: var(--color-secondary);
        font-family: Bariol, sans-serif;
        font-size: 1.375em;
        font-weight: bold;
        letter-spacing: 1.5px;
        text-transform: uppercase;
        white-space: nowrap;
      }
    }
  }

  @media screen and (width >= 1024px) {
    .c-action-banner {
      position: sticky;
      top: 0;
      right: 0;
      left: 0;
    }

    display: flex;
    flex-direction: column;

    .reservation {
      position: sticky;
      top: 6.25em;
      bottom: 0;
      flex: 0 1 30%;
      margin-left: 5em;
    }

    .head {
      height: auto;
      background-color: white;

      img {
        display: none;
      }

      .actions {
        justify-content: flex-end;

        button {
          display: flex;
          flex-direction: row;
          align-items: center;

          &:first-child {
            display: none;
          }

          .c-icon {
            margin-right: 1em;
            color: var(--color-secondary);
            opacity: 0.4;
          }

          .text {
            display: inline-block;
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 1.125em;
            font-weight: 600;
            opacity: 0.4;
          }
        }
      }
    }

    .container {
      max-width: 1400px;
      flex: 0 1 70%;
      flex-direction: row;
      align-items: flex-start;
      justify-content: center;
      padding: 2.5em;

      .content {
        padding: 0 0 2em;

        img {
          display: flex;
          width: 275px;
          height: 175px;
          border-radius: 4px;
          object-fit: cover;
        }

        .illustration-and-heading {
          .heading {
            justify-content: center;
            padding: 0 1.875em;

            h2 {
              span {
                font-size: 2.25em;
                font-weight: bold;
              }
            }

            .golf {
              .name {
                span {
                  font-size: 1.5em;
                }
              }

              a {
                font-size: 1.125em;
              }
            }
          }
        }

        .c-divider {
          margin: 3em 2.5em;
        }

        .conditions,
        .description {
          padding: 0 2.5em;

          .title {
            span {
              font-size: 1.5em;
            }
          }

          .detail {
            span {
              font-weight: normal;
            }
          }
        }
      }
    }

    .c-page-footer {
      width: 100%;
    }
  }

  @media screen and (width >= 1280px) {
    .container {
      .content {
        img {
          width: 325px;
          height: 200px;
        }
      }
    }
  }

  @media screen and (width >= 1440px) {
    .container {
      .content {
        img {
          width: auto;
          max-width: 450px;
          height: 250px;
        }
      }
    }
  }
}
</style>
